import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FormField } from '../components/FormField';
import { Button } from '../components/Button';
import { useAuth } from '../auth/AuthContext';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function mapCognitoError(name?: string): string {
  switch (name) {
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
      return 'Incorrect email or password.';
    case 'UserNotConfirmedException':
      return 'Please verify your email first.';
    case 'PasswordResetRequiredException':
      return 'You need to reset your password.';
    default:
      return 'Sign-in failed. Please try again.';
  }
}

export function Login() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!EMAIL_RE.test(email.trim())) next.email = 'Enter a valid email address.';
    if (!password) next.password = 'Enter your password.';
    setErrors(next);
    if (Object.keys(next).length) return;

    setSubmitting(true);
    try {
      await signIn(email.trim().toLowerCase(), password);
      const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/dashboard';
      navigate(from, { replace: true });
    } catch (err) {
      const name = (err as { name?: string; code?: string }).name ?? (err as { code?: string }).code;
      if (name === 'UserNotConfirmedException') {
        navigate(`/verify?email=${encodeURIComponent(email.trim().toLowerCase())}`);
        return;
      }
      setErrors({ form: mapCognitoError(name) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <h1 style={{ marginBottom: 24 }}>Sign in</h1>

      <FormField
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoComplete="username"
        placeholder="your@email.com"
        error={errors.email}
        required
      />
      <FormField
        label="Password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="current-password"
        placeholder="••••••••"
        error={errors.password}
        required
      />

      {errors.form ? <p style={{ color: 'var(--error)', fontSize: 14 }}>{errors.form}</p> : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
        <Button label="Sign in" type="submit" loading={submitting} />
        <Link to={`/forgot${email ? `?email=${encodeURIComponent(email.trim().toLowerCase())}` : ''}`}
              style={{ textAlign: 'center', color: 'var(--green)', fontSize: 14, padding: 8 }}>
          Forgot password
        </Link>
      </div>
    </form>
  );
}
